import { useState } from 'react';
import api from '../../api/axiosInstance';

export type CategoryMap = {
  발코니: string[];
  일반: string[];
  공틀: string[];
  기타: string[];
  추가항목: string[];
};

export interface UseItemsDataReturn {
  availableItems: string[];
  isItemsLoaded: boolean;
  otherItems: string[];
  tongbaItems: string[];
  additionalItems: string[];
  itemPrices: { [category: string]: { [itemName: string]: { [installationType: string]: number } } };
  getAllItemsData: () => Promise<string[]>;
  categorizeItems: (items: string[]) => CategoryMap;
}

export const categorizeItems = (items: string[]): CategoryMap => {
  const knownItems: CategoryMap = {
    발코니: [
      '발코니_단창_2W',
      '발코니_단창_3W',
      '발코니_이중창_2W',
      '발코니_이중창_3W',
    ],
    일반: [
      '일반_단창_2W',
      '일반_단창_3W',
      '일반_단창_4W',
      '일반_이중창_2W',
      '일반_이중창_3W',
      '일반_이중창_4W',
    ],
    공틀: ['공틀_단창_2W', '공틀_단창_3W', '공틀_단창_4W'],
    기타: ['FIX', '망추가(블랙스텐망)', '랩핑추가'],
    추가항목: [],
  };
  const categories: CategoryMap = {
    발코니: [],
    일반: [],
    공틀: [],
    기타: [],
    추가항목: [],
  };

  const itemsSet = new Set(items);

  (Object.keys(knownItems) as (keyof CategoryMap)[]).forEach((category) => {
    knownItems[category].forEach((item) => {
      if (itemsSet.has(item)) {
        categories[category].push(item);
      }
    });
  });

  const allKnownItems = new Set([
    ...knownItems.발코니,
    ...knownItems.일반,
    ...knownItems.공틀,
    ...knownItems.기타,
  ]);

  items.forEach((item) => {
    if (!allKnownItems.has(item)) {
      categories.추가항목.push(item);
    }
  });

  return categories;
};

export function useItemsData(): UseItemsDataReturn {
  const [availableItems, setAvailableItems] = useState<string[]>([]);
  const [isItemsLoaded, setIsItemsLoaded] = useState<boolean>(false);
  const [otherItems, setOtherItems] = useState<string[]>([]);
  const [tongbaItems, setTongbaItems] = useState<string[]>([]);
  const [additionalItems, setAdditionalItems] = useState<string[]>([]);
  const [itemPrices, setItemPrices] = useState<{
    [category: string]: {
      [itemName: string]: {
        [installationType: string]: number
      }
    }
  }>({});

  const getAllItemsData = async () => {
    try {
      const res = await api.get('/api/items');
      const itemsData = res.data;
      setAvailableItems(itemsData);
      setIsItemsLoaded(true);

      const [categoryRes, pricesRes] = await Promise.all([
        api.get('/api/items-by-category'),
        api.get('/api/item-prices'),
      ]);

      const categoryData = categoryRes.data;
      const pricesDataArray = pricesRes.data;

      const pricesData: { [category: string]: { [itemName: string]: { [installationType: string]: number } } } = {};

      pricesDataArray.forEach((item: any) => {
        const category = item.itemCategory;
        const itemName = item.itemName;
        const installationType = item.installationType;
        const price = item.itemPrice;

        if (!pricesData[category]) {
          pricesData[category] = {};
        }
        if (!pricesData[category][itemName]) {
          pricesData[category][itemName] = {};
        }
        pricesData[category][itemName][installationType] = price;
      });

      setItemPrices(pricesData);

      if (categoryData['중문&기타']) {
        setOtherItems(categoryData['중문&기타']);
      }
      if (categoryData['통바']) {
        setTongbaItems(categoryData['통바']);
      }
      if (categoryData['추가사항']) {
        setAdditionalItems(categoryData['추가사항']);
      }

      return itemsData;
    } catch (error) {
      console.error('품명 목록 로딩 중 오류:', error);
      throw error;
    }
  };

  return {
    availableItems,
    isItemsLoaded,
    otherItems,
    tongbaItems,
    additionalItems,
    itemPrices,
    getAllItemsData,
    categorizeItems,
  };
}
